/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { User, UserRole } from '../domain/entities';
import { Shield, Eye, EyeOff, AlertCircle, Check, User as UserIcon, Mail, Building2, Briefcase } from 'lucide-react';

const ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.SUPER_ADMIN]:     'Platform Admin',
  [UserRole.PLATFORM_OWNER]:  'Platform Owner',
  [UserRole.PLATFORM_SUPPORT]:'Platform Support',
  [UserRole.PLATFORM_SALES]:  'Platform Sales',
  [UserRole.ADMIN]:           'Org Admin',
  [UserRole.DEPARTMENT_HEAD]: 'Dept. Head',
  [UserRole.MANAGER]:         'Manager',
  [UserRole.EMPLOYEE]:        'Employee',
};

export function ProfilePage({
  user,
  orgName,
  onChangePassword,
}: {
  user: User;
  orgName?: string;
  onChangePassword: (currentPwd: string, newPwd: string) => Promise<{ success: boolean; error?: string }>;
}) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const minLength = newPassword.length >= 8;
  const hasNumber = /\d/.test(newPassword);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  const isValid = currentPassword.length > 0 && minLength && hasNumber && passwordsMatch;

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!currentPassword) {
      setError('Please enter your current password.');
      return;
    }
    if (!minLength) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (!hasNumber) {
      setError('New password must contain at least 1 number.');
      return;
    }
    if (!passwordsMatch) {
      setError('New passwords do not match.');
      return;
    }
    if (currentPassword === newPassword) {
      setError('New password must be different from current password.');
      return;
    }

    setLoading(true);
    try {
      const result = await onChangePassword(currentPassword, newPassword);
      if (result.success) {
        setSuccess('Password updated successfully.');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(result.error ?? 'Password change failed.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-8 px-4 space-y-8">
      <div>
        <h1 className="text-2xl font-black text-zinc-900">My Profile</h1>
        <p className="text-sm text-zinc-500 mt-1">View your account details and manage your password.</p>
      </div>

      {/* ── Account Info Card ── */}
      <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
        <div className="bg-zinc-50 border-b border-zinc-200 px-6 py-4">
          <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Account Information</p>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center text-indigo-600 font-black text-xl">
              {user.name.charAt(0)}
            </div>
            <div>
              <h2 className="text-lg font-bold text-zinc-900">{user.name}</h2>
              <p className="text-sm text-zinc-500">{user.email}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div className="flex items-center gap-3 bg-zinc-50 rounded-xl px-4 py-3">
              <Briefcase size={16} className="text-zinc-400" />
              <div>
                <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Role</p>
                <p className="text-sm font-semibold text-zinc-700">{ROLE_LABELS[user.role]}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-zinc-50 rounded-xl px-4 py-3">
              <Building2 size={16} className="text-zinc-400" />
              <div>
                <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Organization</p>
                <p className="text-sm font-semibold text-zinc-700">{orgName ?? 'Platform'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Change Password Card ── */}
      <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
        <div className="bg-zinc-50 border-b border-zinc-200 px-6 py-4 flex items-center gap-3">
          <Shield size={16} className="text-zinc-400" />
          <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Change Password</p>
        </div>
        <form onSubmit={handleChangePassword} className="p-6 space-y-5">
          {/* Current Password */}
          <div className="space-y-1.5">
            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest">
              Current Password
            </label>
            <div className="relative">
              <input
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={e => { setCurrentPassword(e.target.value); setError(''); setSuccess(''); }}
                placeholder="Enter current password"
                autoComplete="current-password"
                className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 pr-11 transition-all"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(p => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                tabIndex={-1}
              >
                {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div className="border-t border-zinc-100" />

          {/* New Password */}
          <div className="space-y-1.5">
            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest">
              New Password
            </label>
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={e => { setNewPassword(e.target.value); setError(''); setSuccess(''); }}
                placeholder="Minimum 8 characters"
                autoComplete="new-password"
                className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 pr-11 transition-all"
              />
              <button
                type="button"
                onClick={() => setShowNew(p => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                tabIndex={-1}
              >
                {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="space-y-1.5">
            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => { setConfirmPassword(e.target.value); setError(''); setSuccess(''); }}
                placeholder="Re-enter new password"
                autoComplete="new-password"
                className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 pr-11 transition-all"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(p => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                tabIndex={-1}
              >
                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {/* Validation checklist */}
          <div className="bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-3 space-y-1.5">
            <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">Requirements</p>
            {[
              { check: minLength, label: 'At least 8 characters' },
              { check: hasNumber, label: 'Contains at least 1 number' },
              { check: passwordsMatch, label: 'Passwords match' },
            ].map(item => (
              <div key={item.label} className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center ${item.check ? 'bg-emerald-500' : 'bg-zinc-200'}`}>
                  {item.check && <Check size={10} className="text-white" />}
                </div>
                <span className={`text-xs ${item.check ? 'text-emerald-700 font-semibold' : 'text-zinc-400'}`}>{item.label}</span>
              </div>
            ))}
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
              <AlertCircle size={14} className="text-red-500 shrink-0" />
              <p className="text-sm text-red-600 font-medium">{error}</p>
            </div>
          )}

          {/* Success */}
          {success && (
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5">
              <Check size={14} className="text-emerald-500 shrink-0" />
              <p className="text-sm text-emerald-600 font-medium">{success}</p>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || !isValid}
            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-zinc-900 text-white rounded-xl font-bold text-sm hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <Shield size={14} /> Update Password
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
